import React, { useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  get, compact, has,
} from 'lodash';
import userMemberships from '../hooks/userMemberships';
import { Context as AppContext } from '../contexts/AppContext';
import { getDuration, dateFormat } from '../lib/utils';
import LogoIcon from "../images/GenrusUnited-Colour.png";

const Base = styled.div`
  display: flex;
  flex-direction: column;
  /* align-items: center; */
  margin: 0.75rem;
  max-width: 55rem;
  width: 100%;

  @media print {
    .title {
      margin-top: 100px;
    }
  }

  p {
    font-size: 0.9em;
  }
  .address {
    /* text-align: center; */
    p {
      line-height: 130%;
      margin: 0;
    }
  }

  .title {
    display: flex;
    align-items: center;
    margin-bottom: 1em;

    .logo {
      max-width: 30rem;
      width: 100%;
      height: 7.5rem;
      -webkit-print-color-adjust: exact;
    }
    .buttons {
      flex: 1;
      text-align: right;
      button {
        color: gray;
      }
      i {
        margin-right: 0.5em;
      }
    }
    @media print {
      .buttons {
        display: none;
      }
    }
}

  .membership-card {
    margin: .5em 0 1.5em;
    @media only screen and (max-width: 500px) {
      div.blank {
        display: none;
      }
    }
  }
`;

const Card = styled.div`
  max-width: 52.5em;
  width: 100%;
  @media print {
    width: 700px;
  }
  border: 1px solid gray;
  border-radius: 0.75rem;
  display: flex;
  .info {
    padding: 0.75rem;
    flex: 1;
  }
  .blank {
    flex: 1;
    border-left: 1px dashed gray;
  }
  .card-logo {
    height: 2.25rem;
    -webkit-print-color-adjust: exact;
    text-align: center;
    img {
      height: 100%;
    }
  }
  .website, .tel, .contact {
    text-align: center;
    font-size: 0.7em;
  }
  .membership {
    margin: 1.5rem 0.75rem;
    font-size: 0.8em;
    line-height: 100%;
    > div {
      display: flex;
      align-items: flex-start;
    }
    label {
      font-size: 0.9em;
      width: 5.25em;
      font-style: italic;
    }
    span {
      font-weight: bold;
      font-family: 'Andale Mono';
    }
    span.membership-number {
      font-size: 2em;
      line-height: 100%;
      @media only screen and (max-width: 340px) {
        font-size: 1.75em;
      }    
    }
  }
  @media print {
    .membership {
      label {
        width: 70px;
      }
    }
  }
  .contact {
    .name {
      font-size: 1.8em;
    }
    .expiry::before {
      content: 'Expiry:';
      margin-right: 0.5em;
    }
  }
`;

export default function Membership({ match, history }) {
  const { client, memberships } = useContext(AppContext);
  const [entity, setEntity] = useState(null);
  const { currentEntity, setCurrentEntityId } = userMemberships({ client });
  const id = get(match, 'params.id');
  useEffect(() => {
    if (!has(memberships, id)) {
      setCurrentEntityId(id);
    } else {
      setEntity(get(memberships, id));
    }
  }, [id, memberships]);
  useEffect(() => {
    if (currentEntity) {
      setEntity(currentEntity);
    }
  }, [currentEntity]);
  const { firstName, lastName } = get(entity, 'user') || {};
  const name = compact([firstName, lastName]).join(' ');
  const { end: expiry } = getDuration(get(entity, 'duration'));
  return (
    <Base>
      <div className="title">
        <div className="logo" />
        <div className="buttons">
          <div className="btn-group btn-group-sm">
            <button type="button" className="btn btn-light" onClick={window.print}>
              <i className="fa fa-print" />
              Print
            </button>
            <button hidden type="button" className="btn btn-light">
              <i className="fa fa-envelope" />
              E-Mail
            </button>
            <button
              type="button"
              className="btn btn-light"
              onClick={() => {
                history.push(`/${match.params.groupId}`);
              }}>
              <i className="fa fa-chevron-left" />
              Back
            </button>
          </div>
        </div>
      </div>
      <p>
        <span>Hello, </span>
        {name}
        <span>,</span>
      </p>
      <p>
        Your Genrus United registration is complete.
        Below is your membership card.
        You may print the card or save this email to present at the pharmacy.
      </p>
      <Card className="membership-card">
        <div className="info">
          <div className="card-logo">
            <img src={LogoIcon} alt="logo"></img>
          </div>
          <div className="website">http://genrusunited.ca</div>
          <div className="tel">T: 1-833-436-7878</div>
          <div className="membership">
            <div>
              <label>Carrier</label>
              <span>GU</span>
            </div>
            <div>
              <label>Group</label>
              <span>{get(entity, 'groupCode') || 'N/A'}</span>
            </div>
            <div>
              <label>Member</label>
              <span className="membership-number">
                {get(entity, 'memberCode') || 'N/A'}
              </span>
            </div>
          </div>
          <div className="contact">
            <div className="name">{name || 'N/A'}</div>
            <div className="expiry">{expiry ? dateFormat(expiry) : 'N/A'}</div>
          </div>
        </div>
        <div className="blank" />
      </Card>
      <p>
        If you have any questions or concerns please visit
        &nbsp;
        <a href="http://genrusunited.ca">http://genrusunited.ca</a>
        &nbsp;
        or contact Genrus United by phone at 1-833-436-7878.
      </p>
      <div className="address">
        <p>Our mailing address:</p>
        <p>14 Court Street, Suite 200</p>
        <p>Box 227</p>
        <p>Truro, Nova Scotia</p>
        <p>B2N 3H7</p>
      </div>
    </Base>
  );
}

Membership.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      groupId: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
};
